import { useEffect, useRef, useState } from 'react';
import type { LiveFeedCapture, LiveFeedFetcher } from './types';

const POLL_INTERVAL_MS = 7_000;
const MAX_BACKOFF_MS = 60_000;
const RING_BUFFER = 500;
const FIRST_PAGE_LIMIT = 50;
const TICK_LIMIT = 200;

export interface LiveFeedState {
  items: LiveFeedCapture[];
  /** "N new" pill counter when scroll is locked. */
  newCount: number;
  /** True when we've had ≥ N consecutive poll failures. */
  reconnecting: boolean;
  /** True when scroll has detached from the top — pauses prepend. */
  scrollLocked: boolean;
}

export interface UseLiveFeedHandle {
  state: LiveFeedState;
  acknowledgeNew(): void;
  setScrollLocked(locked: boolean): void;
}

const EMPTY_STATE: LiveFeedState = {
  items: [],
  newCount: 0,
  reconnecting: false,
  scrollLocked: false,
};

/**
 * Live-feed polling (shared across both admin shells; parameterized by a
 * `fetchLiveFeed`). First fetch seeds items + cursor = serverTime; every 7s
 * polls `?since=<cursor>`, dedupes by id, prepends, caps to RING_BUFFER.
 * Tab hidden → pause; 5xx → exponential backoff to 60s; unmount → abort.
 *
 * FIX vs the original portal hook: switching `conferenceId` now RESETS the
 * item list. The old hook reset only the cursor, so a new conference's captures
 * were prepended onto the previous conference's stale rows ("switching doesn't
 * update the feed"). We clear `items` on every conference change.
 */
export function useLiveFeed(
  conferenceId: string | null,
  fetchLiveFeed: LiveFeedFetcher,
): UseLiveFeedHandle {
  const [state, setState] = useState<LiveFeedState>(EMPTY_STATE);

  const cursorRef = useRef<string | null>(null);
  const intervalRef = useRef<number | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const backoffRef = useRef<number>(POLL_INTERVAL_MS);
  const consecutiveFailuresRef = useRef<number>(0);

  const scrollLockedRef = useRef(state.scrollLocked);
  scrollLockedRef.current = state.scrollLocked;

  function setScrollLocked(locked: boolean): void {
    setState((s) => ({
      ...s,
      scrollLocked: locked,
      newCount: locked ? s.newCount : 0,
    }));
  }

  function acknowledgeNew(): void {
    setState((s) => ({ ...s, newCount: 0 }));
  }

  useEffect(() => {
    if (!conferenceId) {
      setState(EMPTY_STATE);
      return;
    }

    let cancelled = false;
    cursorRef.current = null;
    backoffRef.current = POLL_INTERVAL_MS;
    consecutiveFailuresRef.current = 0;
    // Clear the previous conference's captures so the feed reflects only the
    // newly selected one (the missing reset was the "switch doesn't update" bug).
    setState(EMPTY_STATE);

    async function poll(): Promise<void> {
      if (cancelled) return;
      if (document.visibilityState !== 'visible') return;

      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      try {
        const result = await fetchLiveFeed({
          conferenceId: conferenceId as string,
          since: cursorRef.current,
          limit: cursorRef.current ? TICK_LIMIT : FIRST_PAGE_LIMIT,
          signal: controller.signal,
        });
        if (cancelled) return;

        consecutiveFailuresRef.current = 0;
        backoffRef.current = POLL_INTERVAL_MS;

        setState((s) => {
          const existingIds = new Set(s.items.map((i) => i.id));
          const fresh = result.captures.filter((c) => !existingIds.has(c.id));
          const merged = [...fresh, ...s.items].slice(0, RING_BUFFER);
          return {
            ...s,
            items: merged,
            newCount: scrollLockedRef.current ? s.newCount + fresh.length : 0,
            reconnecting: false,
          };
        });
        cursorRef.current = result.serverTime;
      } catch (err) {
        if ((err as { name?: string }).name === 'AbortError') return;
        if (cancelled) return;
        consecutiveFailuresRef.current += 1;
        backoffRef.current = Math.min(backoffRef.current * 2, MAX_BACKOFF_MS);
        if (consecutiveFailuresRef.current >= 2) {
          setState((s) => ({ ...s, reconnecting: true }));
        }
      } finally {
        if (!cancelled) schedule();
      }
    }

    function schedule(): void {
      if (intervalRef.current) clearTimeout(intervalRef.current);
      intervalRef.current = window.setTimeout(() => void poll(), backoffRef.current);
    }

    function onVisibilityChange(): void {
      if (document.visibilityState === 'visible') {
        if (intervalRef.current) clearTimeout(intervalRef.current);
        void poll();
      }
    }

    void poll();
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      cancelled = true;
      if (intervalRef.current) clearTimeout(intervalRef.current);
      abortRef.current?.abort();
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [conferenceId, fetchLiveFeed]);

  return { state, acknowledgeNew, setScrollLocked };
}
